import classNames from 'classnames';
import { useAppDispatch } from 'hooks/redux';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import type { categoriesTypes } from 'app-components/flux/categories';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/DoneRounded';
import EditIcon from '@mui/icons-material/Edit';
import ThreeDotIcon from '@mui/icons-material/MoreVert';

import DeleteCategoryDialog from './DeleteCategoryDialog';
import UpsertCategoryDialog from './UpsertCategoryDialog';
import { updateCategory } from './utils';

const useStyles = makeStyles()((theme) => ({
  menuItem: {
    display: 'flex',
    color: theme.palette.text.primary,
    alignItems: 'center',
    padding: theme.shape.padding[2],
  },
  highlightedItem: {
    display: 'flex',
    alignItems: 'center',
    padding: 8,
    paddingRight: 8,
    color: theme.palette.primary.main,
  },
  hiddenIcon: {
    marginRight: 16,
    color: theme.palette.text.secondary,
    minWidth: '24px !important',
    opacity: 0,
  },
  highlightedIcon: {
    marginRight: 16,
    minWidth: '24px !important',
    color: theme.palette.primary.main,
  },
  catButton: {
    opacity: 0, // this is used to hide the button when the row is not hovered
    '&:focus, &:hover, &:focus-within': {
      opacity: 1,
    },
  },
}));

type CategoryMenuProps = {
  category: categoriesTypes.Category;
  enableNewCategorySettings: boolean;
  enableTaxItems: boolean;
  enableUsageTypes: boolean;
  automationId: string;
};

const CategoryMenu = (props: CategoryMenuProps) => {
  const { category, enableNewCategorySettings, enableTaxItems, enableUsageTypes, automationId } = props;
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const [menuAnchorEl, setMenuAnchorEl] = useState<Element | null>(null);
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false);
  const [showUpsertCategoryDialog, setShowUpsertCategoryDialog] = useState(false);

  const handleOpenMenu = (event: Event) => {
    // only preventDefault instead of stopProp to allow the dataGridRow to still receive focus/select state from row manager
    event.preventDefault();
    setMenuAnchorEl(event.currentTarget as Element);
  };
  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleDeleteCategory = () => {
    handleCloseMenu();
    setShowDeleteCategoryDialog(true);
  };

  const handleEditCategory = () => {
    handleCloseMenu();
    setShowUpsertCategoryDialog(true);
  };

  const handleToogleExcludeFlag = (type: any) => {
    const value = !category.get(type);
    const params = { [type]: value };

    updateCategory(category, params, dispatch);
    setTimeout(() => {
      handleCloseMenu();
    }, 300);
  };

  let items: ReactNode[] = [
    <MenuItem key='edit' id='menu-edit-category' onClick={handleEditCategory}>
      <ListItemIcon>
        <EditIcon fontSize='small' />
      </ListItemIcon>
      Edit
    </MenuItem>,
    <Divider key='divider_1' />,
  ];

  if (enableNewCategorySettings) {
    items = items.concat([
      <MenuItem
        key='excludeFromSpendingPlan'
        id='menu-ignore-show-sp'
        onClick={() => handleToogleExcludeFlag('isExcludedFromBudgets')}
        classes={{ root: category.isExcludedFromBudgets ? classes.highlightedItem : classes.menuItem }}
      >
        <ListItemIcon classes={{ root: category.isExcludedFromBudgets ? classes.highlightedIcon : classes.hiddenIcon }}>
          <CheckIcon />
        </ListItemIcon>
        Exclude from Spending Plan
      </MenuItem>,
      <MenuItem
        key='excludeFromReports'
        onClick={() => handleToogleExcludeFlag('isExcludedFromReports')}
        id='menu-ignore-show-reports'
        classes={{ root: category.isExcludedFromReports ? classes.highlightedItem : classes.menuItem }}
      >
        <ListItemIcon classes={{ root: category.isExcludedFromReports ? classes.highlightedIcon : classes.hiddenIcon }}>
          <CheckIcon />
        </ListItemIcon>
        Exclude from Reports
      </MenuItem>,
      !category.isNotEditable ? <Divider key='divider_2' /> : null,
    ]);
  }
  if (!category.isNotEditable) {
    items = items.concat([
      <MenuItem key='delete' id='menu-delete-category' onClick={handleDeleteCategory}>
        <ListItemIcon>
          <DeleteIcon fontSize='small' />
        </ListItemIcon>
        Delete
      </MenuItem>,
    ]);
  }

  if (!items.length) return null;

  return (
    <>
      {/* @ts-expect-error TS2769 FIXME */}
      <IconButton
        id={`category-menu-button-${automationId}`}
        onClick={handleOpenMenu}
        // CategoryButton class is selected in CSS from CategoriesSettings
        className={classNames('CategoryButton', classes.catButton)}
      >
        <ThreeDotIcon />
      </IconButton>
      <Menu
        id={`category-menu-${automationId}`}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        {items}
      </Menu>
      {showUpsertCategoryDialog && (
        <UpsertCategoryDialog
          category={category}
          enableNewCategorySettings={enableNewCategorySettings}
          enableTaxItems={enableTaxItems}
          enableUsageTypes={enableUsageTypes}
          onClose={() => setShowUpsertCategoryDialog(false)}
        />
      )}
      {showDeleteCategoryDialog && (
        <DeleteCategoryDialog category={category} onClose={() => setShowDeleteCategoryDialog(false)} />
      )}
    </>
  );
};

export default React.memo(CategoryMenu);
